export function formatCurrencyUsd(amount: number): string {
  return amount.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })
}

/** Figma LOI dashboard — compact rewards (e.g. $12.4K) */
export function formatCompactCurrencyUsd(amount: number): string {
  if (!Number.isFinite(amount) || amount <= 0) return '$0'
  if (amount >= 1_000_000) {
    const v = amount / 1_000_000
    return `$${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}M`
  }
  if (amount >= 1_000) {
    const v = amount / 1_000
    return `$${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}K`
  }
  return formatCurrencyUsd(amount)
}

export function priorityBadgeColor(priority: 'HIGH' | 'MEDIUM' | undefined): string {
  return priority === 'HIGH' ? '#FF5959' : '#F59E0B'
}

export function priorityBadgeBg(priority: 'HIGH' | 'MEDIUM' | undefined): string {
  return priority === 'HIGH' ? '#FAE3ED' : '#FEF3C7'
}

export function insightThresholdMessage(): string {
  return 'AI insights will be available once this instrument reaches 20% of its response target.'
}

/** Progress toward the 20% insight threshold (0–100). */
export function insightThresholdProgressPercent(progressPercent: number): number {
  const pct = Number(progressPercent)
  if (!Number.isFinite(pct) || pct <= 0) return 0
  if (pct >= 20) return 100
  return Math.min(100, Math.round((pct / 20) * 100))
}

/** Avoid React "Objects are not valid as a React child" when API data is malformed. */
export function coerceInsightText(value: unknown, fallback = ''): string {
  if (typeof value === 'string') return value
  if (value == null) return fallback
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  try {
    return JSON.stringify(value)
  } catch {
    return fallback
  }
}

/** Replace statistical N= notation with plain response counts in insight copy. */
export function humanizeInsightSampleSize(text: string): string {
  if (!text) return text

  const formatCount = (count: number): string =>
    count === 1 ? '1 response' : `${count} responses`

  return text
    .replaceAll(/\(N\s*=\s*(\d+)\s*\)/gi, (_, raw) => `(${formatCount(Number(raw))})`)
    .replaceAll(/\bN\s*=\s*(\d+)\s*(responses?)?/gi, (_, raw) => formatCount(Number(raw)))
}

/** User-facing insight / recommendation prose — safe coercion + readable sample sizes. */
export function formatInsightProse(value: unknown, fallback = ''): string {
  return humanizeInsightSampleSize(coerceInsightText(value, fallback))
}

export function formatLastEvaluated(iso: string | undefined): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (!Number.isFinite(date.getTime())) return ''
  const formatted = date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  const [datePart, timePart] = formatted.split(', ')
  return `${datePart}, ${timePart?.replace(' ', ' — ') ?? ''}`
}

/** Insight / recommendation note timestamp — e.g. June 16, 2026 at 6:09 AM */
export function formatInsightNoteTimestamp(iso: string | Date | undefined): string {
  if (!iso) return ''
  const date = iso instanceof Date ? iso : new Date(iso)
  if (!Number.isFinite(date.getTime())) return ''
  const datePart = date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
  const timePart = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  return `${datePart} at ${timePart}`
}

/** Sensemaker note role badge — ADMIN / EDITOR / VIEWER */
export function formatInsightNoteRoleLabel(role: string | undefined): string {
  if (!role) return ''
  if (role === 'ADMIN' || role === 'SensemakerAdmin') return 'ADMIN'
  if (role === 'EDITOR' || role === 'SensemakerEditor') return 'EDITOR'
  if (role === 'VIEWER' || role === 'SensemakerViewer') return 'VIEWER'
  return role.replaceAll('_', ' ')
}

function insightNoteCreatedAtMs(createdAt: string | Date | undefined): number {
  if (!createdAt) return 0
  const date = createdAt instanceof Date ? createdAt : new Date(createdAt)
  const t = date.getTime()
  return Number.isFinite(t) ? t : 0
}

/** Newest note first for dashboard insight / recommendation note lists. */
export function sortInsightNotesNewestFirst<T extends { createdAt?: string | Date }>(
  notes: T[],
): T[] {
  return [...notes].sort(
    (a, b) => insightNoteCreatedAtMs(b.createdAt) - insightNoteCreatedAtMs(a.createdAt),
  )
}

/** Figma LOI dashboard — "Instrument started: Apr 10, 2026" */
export function formatInstrumentStartedDate(iso: string | null | undefined): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (!Number.isFinite(date.getTime())) return null
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/** Figma LOI dashboard — "Completed: Apr 17, 2026" */
export function formatInstrumentCompletedDate(iso: string | null | undefined): string | null {
  return formatInstrumentStartedDate(iso)
}

/** BFP-483 — PARTIAL below 100% target; CONFIRMED only at full target (data completeness). */
export type RecommendationCompletenessStatus = 'CONFIRMED' | 'PARTIAL'

export function recommendationCompletenessStatus(
  progressPercent: number,
  questions?: Array<{ responseCount: number }>,
): RecommendationCompletenessStatus {
  const pct = Number(progressPercent)
  if (!Number.isFinite(pct) || pct < 100) return 'PARTIAL'
  if (questions && questions.length > 0) {
    const allContributed = questions.every((q) => (q.responseCount ?? 0) > 0)
    if (!allContributed) return 'PARTIAL'
  }
  return 'CONFIRMED'
}

/** TC-ID-02 — absolute date/time of last response received. */
export function formatInstrumentDashboardLastUpdated(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    const date = new Date(iso)
    if (!Number.isFinite(date.getTime())) return '—'
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  } catch {
    return '—'
  }
}

/** TC-AI-09 — show Final Analysis when persisted flag is set or target is fully met. */
export function shouldShowFinalAnalysisBadge(
  insight: { isFinalAnalysis?: boolean },
  progressPercent: number,
  questions?: Array<{ responseCount: number }>,
): boolean {
  if (insight.isFinalAnalysis === true) return true
  return recommendationCompletenessStatus(progressPercent, questions) === 'CONFIRMED'
}

/**
 * TC-ID-02 — Last Update shows last response time when a new response arrives;
 * otherwise shows when the dashboard was last fetched (load / refresh → "Just now").
 */
export function resolveDashboardLastUpdated(
  serverIso: string | null | undefined,
  fetchedAtMs: number | undefined,
  previousServerIso: string | null | undefined,
): string | undefined {
  const serverMs = serverIso ? new Date(serverIso).getTime() : Number.NaN
  const prevMs = previousServerIso ? new Date(previousServerIso).getTime() : Number.NaN

  const serverAdvanced =
    Number.isFinite(serverMs) &&
    Number.isFinite(prevMs) &&
    serverMs > prevMs

  if (serverAdvanced && serverIso) {
    return serverIso
  }

  if (fetchedAtMs != null && Number.isFinite(fetchedAtMs) && fetchedAtMs > 0) {
    return new Date(fetchedAtMs).toISOString()
  }

  return serverIso ?? undefined
}

export function formatRelativeLastUpdated(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    const diffMs = Date.now() - new Date(iso).getTime()
    const diffMin = Math.floor(diffMs / 60_000)
    if (diffMin < 1) return 'Just now'
    if (diffMin < 60) return `${diffMin} min ago`
    const diffHr = Math.floor(diffMin / 60)
    if (diffHr < 24) return `${diffHr} hr ago`
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

export function formatQuestionDrillDownLastUpdated(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    const date = new Date(iso)
    if (!Number.isFinite(date.getTime())) return '—'
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  } catch {
    return iso
  }
}
